import { z } from 'zod';
import { languageSchema } from "./languages.js";
import { voiceChoiceSchema } from "./voice.js";
/**
 * Shapes for the REST surface in section 9 of the spec. Both sides import
 * these: the server validates with them, the browser infers its types from
 * them, so a renamed field is a compile error rather than an `undefined` at
 * three in the morning.
 */
export const ROOM_ID_RE = /^[0-9a-f]{12}$/;
export const roomIdSchema = z.string().regex(ROOM_ID_RE, 'malformed room id');
/** 128 bits, hex. Section 9 sets the floor; this pins the exact length. */
export const INVITE_TOKEN_RE = /^[0-9a-f]{32}$/;
export const inviteTokenSchema = z.string().regex(INVITE_TOKEN_RE, 'malformed invite');
export const iceServerSchema = z.object({
    urls: z.union([z.string(), z.array(z.string())]),
    username: z.string().optional(),
    credential: z.string().optional(),
});
export const iceConfigSchema = z.object({
    iceServers: z.array(iceServerSchema),
    /**
     * `relay` forces every candidate through TURN. The spec requires proving the
     * relay path works, and the only honest way to prove it is to forbid the
     * direct one for that run.
     */
    iceTransportPolicy: z.enum(['all', 'relay']),
    /** Unix ms. TURN credentials are short-lived; the client refreshes before this. */
    expiresAt: z.number().int().positive(),
});
export const roomLimitsSchema = z.object({
    maxCallSeconds: z.number().int().positive(),
    inviteTtlSeconds: z.number().int().positive(),
    reconnectGraceSeconds: z.number().int().positive(),
    emptyRoomTtlSeconds: z.number().int().positive(),
});
export const createRoomRequestSchema = z.object({
    /** The creator's own language. They will hear everything in it. */
    myLanguage: languageSchema,
    /** The other side's language. Fixed for the lifetime of the room. */
    peerLanguage: languageSchema,
    /** Shared gate for room creation. Ignored when the server has none set. */
    accessCode: z.string().max(200).optional(),
    /**
     * Whether to also show each speaker's own words, not just the translation.
     *
     * A request, not an instruction: the provider charges per minute for it, so
     * the server has the last word and a deployment with it switched off hands
     * back `false`. Fixed for the room's lifetime, like the language pair —
     * both sides have to agree about what the session was opened for.
     */
    sourceSubtitles: z.boolean().optional(),
    /**
     * The voice each side hears the translation in (task 6). `mine` is what the
     * creator hears, `peer` what the guest hears — the creator decides both, as
     * with the language pair.
     *
     * Also a request: a deployment without an ElevenLabs key answers with the
     * built-in voice for both, and absent means the built-in voice.
     */
    voices: z.object({ mine: voiceChoiceSchema, peer: voiceChoiceSchema }).optional(),
});
export const participantViewSchema = z.object({
    roomId: roomIdSchema,
    /** 0 is the creator and the P2P offerer; 1 is the joiner and the polite peer. */
    seat: z.union([z.literal(0), z.literal(1)]),
    myLanguage: languageSchema,
    peerLanguage: languageSchema,
    iceConfig: iceConfigSchema,
    limits: roomLimitsSchema,
    /** What the room was actually opened with, after the server's own limit. */
    sourceSubtitles: z.boolean(),
    /**
     * The voice *this* seat hears the translation in, after the server's own
     * limit. Only this seat's: the guest is not shown what the creator picked
     * for either side, and has no use for the other seat's.
     *
     * Optional because a page can outlive the server that served it: during a
     * two-slot deploy a new bundle may be answered by an older instance.
     */
    voice: voiceChoiceSchema.optional(),
    /**
     * What the *other* seat hears. Given to the creator only, who chose it and
     * whose call screen says so; the guest is never told either side's voice.
     */
    peerVoice: voiceChoiceSchema.optional(),
    /**
     * Mirrors the HttpOnly cookie. Tests and non-browser clients need a way in;
     * the browser app never reads it and never stores it.
     */
    participantToken: z.string(),
    expiresAt: z.number().int().positive(),
});
export const createRoomResponseSchema = participantViewSchema.extend({
    inviteToken: inviteTokenSchema,
});
export const joinRoomRequestSchema = z.object({
    /** The expiring invitation is the guest's credential; no shared code is needed. */
    inviteToken: inviteTokenSchema,
});
/** What a joiner sees before committing: the pair, so they can bail early. */
export const roomPreviewSchema = z.object({
    roomId: roomIdSchema,
    /** Languages as the *creator* set them, from the joiner's point of view. */
    myLanguage: languageSchema,
    peerLanguage: languageSchema,
    seatsTaken: z.number().int().min(0).max(2),
    /** Always false; retained for older clients that still display an access-code field. */
    accessCodeRequired: z.boolean(),
    /** So a joiner knows before committing whether they will see the original. */
    sourceSubtitles: z.boolean(),
});
/**
 * Empty on purpose. The target language and the provider come from server
 * config keyed by the caller's seat — section 9 forbids taking either from the
 * request body, or a participant could aim someone else's translator.
 */
export const translationSessionRequestSchema = z.object({}).strict();
export const translationSessionResponseSchema = z.object({
    provider: z.literal('openai'),
    model: z.string(),
    expiresAt: z.number().int().positive(),
    targetLanguage: languageSchema,
    sourceLanguage: languageSchema,
    /**
     * Same-origin path the browser POSTs its SDP offer to; the server forwards
     * it to the provider and hands back the answer.
     *
     * The ephemeral secret is deliberately absent. It stays on the server, which
     * means the browser never holds a provider credential at all — and the one
     * request that used to leave the browser for the provider's API now does
     * not, which matters because that API is geo-blocked for some participants.
     * The audio is unaffected: it is still WebRTC straight to the provider.
     */
    sdpUrl: z.string(),
    /** Server-side id so telemetry from both ends can be lined up. */
    sessionId: z.string(),
});
export const telemetryEventSchema = z.object({
    /** Free-form but bounded; the server never interprets it as a command. */
    name: z.string().min(1).max(64),
    at: z.number().int().positive(),
    /** Numbers only. Transcript text must never reach the server. */
    values: z.record(z.string().max(48), z.number().finite()).optional(),
    tags: z.record(z.string().max(48), z.string().max(120)).optional(),
});
export const telemetryRequestSchema = z.object({
    roomId: roomIdSchema.optional(),
    sessionId: z.string().max(64).optional(),
    events: z.array(telemetryEventSchema).min(1).max(50),
});
/**
 * The second channel to the server (task 4), beside telemetry and deliberately
 * unlike it.
 *
 * Telemetry is numbers and short tags on a ten-second timer: small, frequent,
 * and cheap enough that nobody thinks about it. What tasks 5 and 6 need is the
 * opposite shape — fewer, larger payloads that describe one part of a call, or
 * name an object already sitting in the recordings bucket — and mixing the two
 * would force one schema to be loose enough for both. So this is its own
 * endpoint with its own schema, and the old one keeps its promise that there
 * is nowhere in it to put a transcript even by accident.
 *
 * Section 9 is the reason the two are separated rather than merged. The rule
 * there is that speech never reaches the server; tasks 5 and 6 are the
 * deliberate reversal of it, and task 8 — a consent screen both participants
 * see — is the condition. This schema therefore describes what the channel
 * will carry, and the server refuses the one member of it that is speech.
 */
/**
 * The batch's size ceiling, chosen by the weakest transport that has to carry
 * it. `navigator.sendBeacon` is the only way to get a payload off a page that
 * is already going away, and browsers cap what it will accept at 64 KiB;
 * over that it returns false and the data is simply gone. Staying under the
 * cap with room to spare is worth more than a bigger batch, because the batch
 * that matters most is the last one.
 */
export const INGEST_MAX_BYTES = 56 * 1024;
/** A second ceiling, on count, so one batch cannot be a thousand tiny items. */
export const INGEST_MAX_ITEMS = 50;
/**
 * Whatever the provider says a part cost, as numbers under the provider's own
 * names. Numbers only, exactly as in telemetry: a map that accepted strings
 * would be a hole in section 9 the size of a transcript.
 *
 * It is a map rather than named fields because nobody yet knows what the
 * provider reports — task 1 is still finding out, and a translation session
 * has no `response.done` to read a token count from. If the answer turns out
 * to be "no tokens, only audio seconds", this carries `audioMs` and nothing
 * else changes. The server promotes the handful of keys it understands into
 * columns and keeps the rest verbatim.
 */
export const ingestUsageSchema = z.record(z.string().max(48), z.number().finite());
/**
 * One part of a call, as the *client* defines a part.
 *
 * The schema deliberately does not say where a part begins or ends. The
 * provider's translation stream has no completion marker — it is a continuous
 * flow of deltas with no turn-taking — so any boundary is a rule somebody
 * chose, and until task 1 reports what the provider actually emits, choosing
 * one here would be inventing it. `boundary` records which rule produced this
 * row, so parts measured under a rule we later abandon stay interpretable
 * instead of silently mixing with parts measured under its replacement.
 */
export const ingestPartSchema = z.object({
    kind: z.literal('part'),
    /** Monotonic within a session. With the session id it identifies the row. */
    seq: z.number().int().min(0),
    /**
     * `source -> target` as the client saw it. Redundant with the session row
     * today, because a session translates one way for its whole life; kept
     * because a provider that ever translates both ways in one session would
     * otherwise have nowhere to say so.
     */
    direction: z.string().max(24).optional(),
    /** The client's name for the rule that ended this part. Free-form. */
    boundary: z.string().max(24).optional(),
    /** Wall clock, for ordering and for joining to a day. See below on clocks. */
    startedAt: z.number().int().positive(),
    /**
     * Durations, not timestamps, and measured inside one page against one clock.
     * Section 10.2 forbids subtracting timestamps taken in different tabs or on
     * different devices, so the differences are computed where they are honest
     * and only the results travel.
     */
    firstOutputMs: z.number().int().min(0).max(3_600_000).optional(),
    durationMs: z.number().int().min(0).max(3_600_000).optional(),
    usage: ingestUsageSchema.optional(),
    /**
     * What the client believes this part cost, when it can work it out at all.
     * Usually it cannot: the rate is not in the API, and a number computed from
     * a rate we guessed would be worse than a null. Reconciling against the
     * provider's invoice is the point, and a fabricated figure defeats it.
     */
    costUsd: z.number().finite().nonnegative().optional(),
});
/**
 * A reference to an object the browser has already uploaded to the bucket.
 *
 * Only a reference. The bytes go browser → bucket over a presigned URL and
 * never through this process (see `storage/objects.ts`), so what arrives here
 * is a key, a size and a duration — small enough that a hundred of them fit in
 * one beacon.
 */
export const ingestRecordingSchema = z.object({
    kind: z.literal('recording'),
    /** The key the browser PUT to. The server signed it, so it is not a claim. */
    objectKey: z.string().min(1).max(512),
    /** `audio-original`, `audio-translated`, `transcript`… free-form on purpose. */
    mediaKind: z.string().min(1).max(32),
    bytes: z.number().int().min(0),
    durationMs: z
        .number()
        .int()
        .min(0)
        .max(24 * 3_600_000)
        .optional(),
    /** Which chunk of a recording split across several objects, when it is one. */
    seq: z.number().int().min(0).optional(),
    contentType: z.string().max(80).optional(),
    at: z.number().int().positive(),
});
/**
 * Speech. Inert, and meant to stay inert until task 8 ships.
 *
 * It is defined here, and refused by the server, on purpose. Task 5 will send
 * subtitles to the server and that reverses the rule section 9 is built on, so
 * the shape is settled now, in the open, where a reviewer can see exactly what
 * the channel would carry — rather than being invented in a hurry next to the
 * consent screen that is supposed to authorise it. Until both participants
 * have agreed on that screen, `/api/ingest` counts these as refused and writes
 * nothing. Nothing in the browser app constructs one.
 */
export const ingestTranscriptSchema = z.object({
    kind: z.literal('transcript'),
    seq: z.number().int().min(0),
    /** `original` is what was said, `translation` is what the listener heard. */
    direction: z.enum(['original', 'translation']),
    text: z.string().max(2000),
    language: languageSchema,
    at: z.number().int().positive(),
});
export const ingestItemSchema = z.discriminatedUnion('kind', [
    ingestPartSchema,
    ingestRecordingSchema,
    ingestTranscriptSchema,
]);
export const ingestBatchSchema = z.object({
    roomId: roomIdSchema,
    /**
     * The provider session everything in the batch belongs to. Optional because
     * a recording can outlive the session that produced it — the upload finishes
     * after the call has ended — and a row with a null session is still worth
     * having. `stats.sql` has a query for exactly those.
     */
    sessionId: z.string().max(64).optional(),
    items: z.array(ingestItemSchema).min(1).max(INGEST_MAX_ITEMS),
});
/**
 * What the endpoint answers. The browser mostly cannot read it — a beacon sent
 * during unload has no one left to hand a response to — so it exists for
 * whoever is debugging with curl, and for the tests. `refused` is how a
 * transcript item that was quietly dropped becomes visible.
 */
export const ingestResponseSchema = z.object({
    accepted: z.number().int().min(0),
    refused: z.number().int().min(0),
});
export const healthResponseSchema = z.object({
    ok: z.boolean(),
    version: z.string(),
    uptimeSeconds: z.number(),
    rooms: z.number().int().min(0),
    /** Whether a translation provider key is configured. Never the key itself. */
    provider: z.object({ name: z.string(), configured: z.boolean() }),
    /**
     * The session-statistics store. `enabled` is whether one is configured at
     * all; `connected` is whether it last answered. Never the connection
     * string, which would carry a password.
     */
    store: z.object({ enabled: z.boolean(), connected: z.boolean() }),
    /**
     * The recordings bucket. `enabled` is whether one is configured; the name is
     * a bucket, not a credential, and knowing it is what tells a deploy apart
     * from the one that is quietly writing nowhere.
     */
    objects: z.object({ enabled: z.boolean(), bucket: z.string() }),
    /**
     * Which half of a two-slot deploy this is, and whether it has been handed
     * the door to a successor. A deploy watches `draining` and `rooms` to know
     * when the old instance has finished its last call.
     */
    instance: z.object({ slot: z.string(), draining: z.boolean() }),
});
export const errorResponseSchema = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
    }),
});
//# sourceMappingURL=http.js.map